import { FormValidation, UiSchema } from '@rjsf/utils'

import { calculateLFMeasurementOfMaterial } from '../imup/sagas/2D/createDrawableLocation'
import { FormProperties } from '../imup/types'
import { BEAM_TYPES } from '../imup/utils/constants'
import { ActiveDrawable } from '../models/activeDrawable'
import { GeneralDrawableSettings } from '../models/activeDrawableSettings'
import { DRAWABLE_TYPES } from '../shared/constants/drawable-types'
import { otherValue } from '../shared/constants/general-enums/value-other'
import { convertFullNumberFractionToDecimal, truncateToDecimals } from '../utils/formSchema/numberConversion'
import { applyOtherDimensionValues } from '../utils/formSchema/otherDimensions'

export const uiSchema: UiSchema = {
    'ui:order': [
        'beam_type',
        'beam_material',
        'ewp_type',
        'dimensional_beam_dimensions',
        'ewp_beam_dimensions',
        'beam_thickness_other',
        'beam_width_other',
        'bearing',
        'plies',
        'plies_other',
        'pitch_1',
        'pitch_other_1',
        'pitch_2',
        'pitch_other_2',
    ],
    beam_type: {
        'ui:placeholder': 'Select Beam Type',
    },
    beam_material: {
        'ui:placeholder': 'Select Beam Material',
    },
    ewp_type: {
        'ui:placeholder': 'Select EWP Type',
    },
    dimensional_beam_dimensions: {
        'ui:field': 'LumberDimensionsField',
    },
    ewp_beam_dimensions: {
        'ui:field': 'LumberDimensionsField',
    },
    beam_thickness_other: {
        'ui:title': 'Other Thickness',
        'ui:placeholder': 'Enter Other Thickness',
    },
    beam_width_other: {
        'ui:title': 'Other Width',
        'ui:placeholder': 'Enter Other Width',
    },
    plies: {
        'ui:title': 'Plies',
        'ui:placeholder': 'Select Plies',
    },
    plies_other: {
        'ui:title': 'Other Plies',
        'ui:placeholder': 'Enter Other Plies',
    },
    bearing: {
        'ui:placeholder': 'Select Bearing',
    },
    pitch_1: {
        // this property name is used by request from material calc.
        'ui:placeholder': 'Select Pitch', // title has been omitted in order for the beam tool to use the same definition with a different title. (Ex: Pitch vs Pitch 1)
    },
    pitch_other_1: {
        'ui:placeholder': 'Enter Other Pitch',
    },
    pitch_2: {
        'ui:placeholder': 'Select Pitch',
    },
    pitch_other_2: {
        'ui:placeholder': 'Enter Other Pitch',
    },
}

const onValidate = (formData: any, errors: FormValidation) => {
    if (formData.beam_material === 'EWP') {
        if (formData.ewp_beam_dimensions?.thickness === 'other' && formData.beam_thickness_other) {
            const beam_thickness_other = Number(convertFullNumberFractionToDecimal(formData.beam_thickness_other))

            if (beam_thickness_other && beam_thickness_other >= 100) {
                errors.beam_thickness_other?.addError('Must be less than 100')
            }
        }
        if (formData.ewp_beam_dimensions?.width === 'other' && formData.beam_width_other) {
            const ewp_width_other = Number(convertFullNumberFractionToDecimal(formData.beam_width_other))

            if (ewp_width_other && ewp_width_other >= 100) {
                errors.beam_width_other?.addError('Must be less than 100')
            }
        }
    }
}

export const transformBeamSetting = (settings: GeneralDrawableSettings) => {
    let newSettings = applyOtherDimensionValues(settings, {
        dimensionsKey: 'dimensional_beam_dimensions',
        thicknessOtherKey: 'beam_thickness_other',
        widthOtherKey: 'beam_width_other',
    })

    newSettings = applyOtherDimensionValues(newSettings, {
        dimensionsKey: 'ewp_beam_dimensions',
        thicknessOtherKey: 'beam_thickness_other',
        widthOtherKey: 'beam_width_other',
        shouldConvertFractions: true,
    })

    if (newSettings.plies === otherValue) {
        newSettings.plies = newSettings.plies_other
    }
    newSettings.plies_other = null

    if (newSettings.pitch_1 === otherValue) {
        const pitchOther1 = String(newSettings.pitch_other_1)

        newSettings.pitch_1 = truncateToDecimals(convertFullNumberFractionToDecimal(pitchOther1) ?? pitchOther1)
    }
    newSettings.pitch_other_1 = null

    if (newSettings.pitch_2 === otherValue) {
        const pitchOther2 = String(newSettings.pitch_other_2)

        newSettings.pitch_2 = truncateToDecimals(convertFullNumberFractionToDecimal(pitchOther2) ?? pitchOther2)
    }
    newSettings.pitch_other_2 = null

    return newSettings
}

const onSubmit = (settings, formData) => {
    const newSettings = { ...settings, ...formData }

    return transformBeamSetting(newSettings)
}

function prepareDrawables(input: {
    drawables: ActiveDrawable[]
    newSettings: GeneralDrawableSettings
    scaleFactor: number
}): ActiveDrawable[] {
    const { drawables, newSettings } = input

    return drawables.map((drawable) => {
        if ([BEAM_TYPES.HIP_AND_VALLEY_BEAM, BEAM_TYPES.FLUSH_BEAM].includes(newSettings.beam_type as BEAM_TYPES)) {
            drawable.settings.quantity = calculateLFMeasurementOfMaterial(drawable.settings.linear_total, {
                type: DRAWABLE_TYPES.BEAM,
                settings: newSettings,
            })
        } else if (drawable.settings.quantity !== drawable.settings.linear_total) {
            drawable.settings.quantity = drawable.settings.linear_total
        }

        return drawable
    })
}

const form: FormProperties = { uiSchema, onValidate, onSubmit, prepareDrawables }

export default form
